import { useEffect, useState } from "react";
import AxiosInstance from "../config/AxiosInstance";
import { fieldErrorText } from "./apiError";

/**
 * Input limits served by the API (DTO rules merged with column sizes), shared byte-for-byte by
 * finex-web and inventory-web. Forms read them instead of copying numbers onto inputs, so a
 * limit changes in one place. If the request fails the form behaves as before; the API still
 * enforces every rule on save.
 */

export interface FieldLimit {
  maxLength?: number;
  minLength?: number;
  min?: number;
  max?: number;
  required?: boolean;
  format?: "email";
}
export type FieldLimits = Record<string, FieldLimit>;

type Lang = Record<string, string> | null | undefined;

/** Forms saved through core-api, e.g. coreFormLimits("client"). */
export const coreFormLimits = (form: string) => `/api/meta/field-limits/${form}`;
/** Forms saved through inventory-api, e.g. inventoryFormLimits("item"). */
export const inventoryFormLimits = (form: string) => `/api/inventory/meta/field-limits/${form}`;

const cache = new Map<string, Promise<FieldLimits>>();

export const loadFieldLimits = (path: string): Promise<FieldLimits> => {
  if (!cache.has(path)) {
    cache.set(
      path,
      AxiosInstance.get(path)
        .then((response) => (response?.data?.fields ?? {}) as FieldLimits)
        .catch(() => {
          cache.delete(path);
          return {};
        }),
    );
  }
  return cache.get(path) as Promise<FieldLimits>;
};

export const useFieldLimits = (path: string): FieldLimits => {
  const [limits, setLimits] = useState<FieldLimits>({});
  useEffect(() => {
    let alive = true;
    loadFieldLimits(path).then((loaded) => {
      if (alive) setLimits(loaded);
    });
    return () => {
      alive = false;
    };
  }, [path]);
  return limits;
};

/** Spread onto an <input>: maxLength for text, min/max for numbers. */
export const limitProps = (limits: FieldLimits, field: string, kind: "text" | "number" = "text") => {
  const limit = limits[field];
  if (!limit) return {};
  if (kind === "number") {
    return {
      ...(limit.min !== undefined ? { min: limit.min } : {}),
      ...(limit.max !== undefined ? { max: limit.max } : {}),
    };
  }
  return limit.maxLength ? { maxLength: limit.maxLength } : {};
};

const isBlank = (value: unknown) => value === undefined || value === null || String(value).trim() === "";

/**
 * The API's checks run before saving, with the same per-field wording the API's errors get.
 * Pass `fields` to check only those (e.g. the current step) and to include their required checks:
 * without a list only lengths, ranges and formats are checked, so a field the form fills in code
 * (companyId, statusId, ...) can never block a save.
 */
export const validateAgainstLimits = (
  values: Record<string, unknown>,
  limits: FieldLimits,
  language?: Lang,
  fields?: string[],
): Record<string, string> => {
  const errors: Record<string, string> = {};
  for (const field of fields ?? Object.keys(limits)) {
    const limit = limits[field];
    if (!limit) continue;
    const value = values[field];
    const fail = (code: string, params: Record<string, unknown> = {}) => {
      errors[field] = fieldErrorText({ code, params, message: "" }, language);
    };
    if (isBlank(value)) {
      if (limit.required && fields) fail("isNotEmpty");
      continue;
    }
    const text = String(value);
    // The raw value in UTF-16 units, as the input's own maxLength and FieldCounter count it.
    if (limit.maxLength && text.length > limit.maxLength) fail("maxLength", { max: limit.maxLength });
    else if (limit.minLength && text.length < limit.minLength) fail("minLength", { min: limit.minLength });
    else if (limit.format === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text)) fail("isEmail");
    else if (limit.max !== undefined && Number(value) > limit.max) fail("max", { max: limit.max });
    else if (limit.min !== undefined && Number(value) < limit.min) fail("min", { min: limit.min });
  }
  return errors;
};
